import React, { useEffect, useRef, useState } from 'react';
import { sceneChipFace, sceneChipNeedsWords, sceneChipHint } from '../../utils/sceneCommands';

// 🎛️ 10-09 (Nina: the event buttons "should be able to work while the user is on the full scene
// page (not the chat tab)"): the event's buttons on the full-screen picture, under Back. The
// chips and what they send are the message box's own (utils/sceneCommands, the page's
// onSceneCommand). Solid pills like Back and Wish (translucent controls vanish over art);
// inline styles only (the House theme styles by class-name substring). The house's answer shows
// HERE: the chat's notice line that carries it sits under the picture.
const PARCH = '#e6dac6';
const GOLD = '#e9b565';
const INK = '#2a1c08';
const COMMAND_MAX = 200;
const NOTE_MS = 7000; // the chat's own notice line clears at 7 s
const stop = (e) => e.stopPropagation();
const sorry = "That didn't go through. Try again.";

const pill = (lit) => ({
  height: 34, padding: '0 13px 0 11px', display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999,
  cursor: 'pointer', font: 'inherit', fontSize: 13.5, fontWeight: 700, lineHeight: 1, textShadow: 'none', whiteSpace: 'nowrap',
  background: lit ? GOLD : '#2c2117', border: `1px solid ${GOLD}`, color: lit ? INK : PARCH, boxSizing: 'border-box',
  boxShadow: '0 2px 10px rgba(0,0,0,0.45)', touchAction: 'manipulation',
});
// the house's words (a refusal, the maze's "Dead end", who's where) in gold; a failed send in coral
const noteStyle = (tone) => ({
  padding: '6px 10px', borderRadius: 10, background: 'rgba(28,21,15,0.92)', fontSize: 12.5, fontWeight: 700, lineHeight: 1.35,
  border: tone === 'error' ? '1px solid rgba(255,159,122,0.55)' : `1px solid ${GOLD}`, color: tone === 'error' ? '#ffd2bf' : PARCH,
});
/** A resolved press → { ok, line } (the page's sceneAnswer). Anything else (Carve's own open) went through. */
const answerOf = (r) => (r && typeof r === 'object'
  ? { ok: r.ok !== false, line: typeof r.line === 'string' ? r.line.trim() : '' }
  : { ok: true, line: '' });

/** The row under Back. A tap on a chip that needs no words sends it: ✓ when the house took it,
 *  the house's own line when it answered (a refusal gets no ✓), the error when the send failed.
 *  A chip that needs words asks for them (`onAsk`, the picture's bottom box); `asking` = the
 *  chip whose box is open (it stays lit). */
export function SceneCommands({ chips, onCommand, onAsk = null, asking = null }) {
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);
  const [note, setNote] = useState(null); // { text, tone: 'said' | 'error' }
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(() => { if (alive.current) setDone(null); }, 1600);
    return () => clearTimeout(id);
  }, [done]);
  useEffect(() => {
    if (!note || note.tone !== 'said') return undefined; // a failed send stays until the next tap
    const id = setTimeout(() => { if (alive.current) setNote(null); }, NOTE_MS);
    return () => clearTimeout(id);
  }, [note]);
  if (!Array.isArray(chips) || !chips.length) return null;
  const tap = async (chip) => {
    if (busy) return;
    setNote(null);
    if (sceneChipNeedsWords(chip)) { if (onAsk) onAsk(chip); return; }
    if (!onCommand) return;
    setBusy(chip);
    try {
      const { ok, line } = answerOf(await onCommand(chip, ''));
      if (alive.current) {
        if (ok) setDone(chip);
        if (line) setNote({ text: line, tone: 'said' });
      }
    } catch (e) {
      // a quiet error = the app's own sign-in door took over: the row says nothing
      if (alive.current && !(e && e.quiet)) setNote({ text: String((e && e.message) || sorry), tone: 'error' });
    } finally {
      if (alive.current) setBusy(null);
    }
  };
  return (
    <div data-peek-commands="1" onClick={stop} onPointerDown={stop} onPointerUp={stop} onPointerMove={stop}
      style={{ position: 'absolute', top: 52, left: 10, maxWidth: 'calc(50% - 15px)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
      {chips.map((chip) => {
        const f = sceneChipFace(chip);
        return (
          <button key={chip} type="button" data-peek-command={f.key} aria-label={f.label} title={f.label}
            aria-busy={busy === chip ? 'true' : undefined}
            onClick={(e) => { e.stopPropagation(); tap(chip); }}
            style={{ ...pill(done === chip || asking === chip), opacity: busy && busy !== chip ? 0.6 : 1 }}>
            {f.emoji ? <span aria-hidden="true">{f.emoji}</span> : null}
            {f.word}
            {busy === chip ? <span aria-hidden="true">…</span> : null}
            {done === chip ? <span aria-hidden="true">✓</span> : null}
          </button>
        );
      })}
      {note ? (
        <div role="status" data-peek-command-note={note.tone} data-peek-command-error={note.tone === 'error' ? '1' : undefined}
          style={{ flexBasis: '100%', ...noteStyle(note.tone) }}>
          {note.text}
        </div>
      ) : null}
    </div>
  );
}

/** The one-line box for a chip that needs words (the Wish box's shape): Enter sends the chip
 *  with its words through `onCommand(chip, words)`. Taken = it says so, then closes; the house
 *  said no = its line over the box and the words stay (change them or close); a failed send =
 *  the error over the box. `docked` = the phone scene's bottom dock, else over the picture's
 *  bottom edge. */
export function CommandBar({ chip, onCommand, onClose, docked = false }) {
  const [text, setText] = useState('');
  const [state, setState] = useState(null); // null | 'sending' | 'sent'
  const [note, setNote] = useState(null); // { text, tone: 'said' | 'error' }: the house's words over the box
  const inputRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => { try { if (inputRef.current) inputRef.current.focus(); } catch (e) { /* focus is a nicety */ } }, [chip]);
  useEffect(() => {
    if (state !== 'sent') return undefined;
    // a line riding a taken press (who's where) stays long enough to read
    const id = setTimeout(() => { if (onClose) onClose(); }, note ? NOTE_MS : 2000);
    return () => clearTimeout(id);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  const f = sceneChipFace(chip);
  const clean = text.replace(/\s+/g, ' ').trim();
  const send = async () => {
    if (!clean || state === 'sending' || !onCommand) return;
    setState('sending');
    setNote(null);
    try {
      const { ok, line } = answerOf(await onCommand(chip, clean.slice(0, COMMAND_MAX)));
      if (!alive.current) return;
      if (line) setNote({ text: line, tone: 'said' });
      if (ok) { setText(''); setState('sent'); } else setState(null); // the house said no: the words stay
    } catch (e) {
      if (!alive.current) return;
      setState(null);
      if (!(e && e.quiet)) setNote({ text: String((e && e.message) || sorry), tone: 'error' }); // quiet = the app's sign-in door took over
    }
  };
  const close = (
    <button type="button" aria-label="Close" onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
      style={{ flex: '0 0 auto', width: 28, height: 28, padding: 0, borderRadius: 999, border: 0, cursor: 'pointer', background: 'transparent', color: PARCH, font: 'inherit', fontSize: 15, lineHeight: 1, textShadow: 'none' }}>
      ✕
    </button>
  );
  return (
    <div data-peek-command-bar={f.key} onClick={stop} onPointerDown={stop} onPointerUp={stop} onPointerMove={stop}
      style={{
        ...(docked ? { position: 'relative', width: '100%' } : { position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)', width: 'min(460px, calc(100% - 20px))' }),
        boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'auto', touchAction: 'auto', userSelect: 'text', WebkitUserSelect: 'text',
      }}>
      {note ? (
        <div role="status" data-peek-command-note={note.tone} style={{ ...noteStyle(note.tone), alignSelf: 'flex-start', maxWidth: '100%', boxSizing: 'border-box' }}>
          {note.text}
        </div>
      ) : null}
      <div style={{
        boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 12px', minHeight: 40, borderRadius: 999,
        background: '#2c2117', border: `1px solid ${GOLD}`, boxShadow: '0 4px 18px rgba(0,0,0,0.6)',
      }}>
        {state === 'sent' ? (
          <>
            <span aria-live="polite" style={{ flex: '1 1 auto', color: GOLD, fontSize: 14, fontWeight: 700, padding: '6px 0' }}>{f.word}: sent.</span>
            {close}
          </>
        ) : (
          <>
            <span aria-hidden="true" style={{ flex: '0 0 auto', color: GOLD, fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap' }}>{f.emoji ? `${f.emoji} ` : ''}{f.word}</span>
            <input ref={inputRef} type="text" aria-label={f.label} placeholder={`${sceneChipHint(chip)}…`} maxLength={COMMAND_MAX}
              value={text} onChange={(e) => { setText(e.target.value); if (note) setNote(null); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !(e.nativeEvent && e.nativeEvent.isComposing)) { e.preventDefault(); send(); }
                if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (onClose) onClose(); }
              }}
              style={{ flex: '1 1 auto', minWidth: 0, width: '100%', boxSizing: 'border-box', height: 32, padding: '0 2px', border: 0, outline: 'none', background: 'transparent', color: PARCH, font: 'inherit', fontSize: 16 }} />
            {close}
            <button type="button" onClick={(e) => { e.stopPropagation(); send(); }} disabled={!clean || state === 'sending'}
              style={{
                flex: '0 0 auto', height: 32, padding: '0 14px', borderRadius: 999, border: 0, cursor: clean ? 'pointer' : 'default', boxSizing: 'border-box',
                background: GOLD, color: INK, font: 'inherit', fontSize: 13.5, fontWeight: 800, textShadow: 'none', opacity: (!clean || state === 'sending') ? 0.55 : 1,
              }}>
              {state === 'sending' ? '…' : 'Send'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
